<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Campanhas</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">Acompanhe os disparos, filtre resultados e visualize o desempenho das campanhas.</p>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
        <div v-for="card in cards" :key="card.title" class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-gray-500">{{ card.title }}</p>
              <h2 class="mt-2 text-3xl font-medium text-gray-900">{{ card.value }}</h2>
            </div>
            <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100">
              <heroicon :name="card.icon" class="h-6 w-6" :class="card.iconClass" style="fill: currentColor" />
            </div>
          </div>
        </div>
      </div>

      <div class="mb-6 flex justify-end">
        <router-link :to="`/${route}/import`" class="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 py-3 shadow-sm transition hover:shadow-md sm:w-auto">
          <heroicon name="plus-circle" class="h-6 w-6 text-green-600" style="fill: currentColor" />
          <span class="text-sm font-medium text-gray-700">Importar campanha</span>
        </router-link>
      </div>

      <div class="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div class="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div class="flex flex-col gap-1">
            <label for="busca" class="block text-sm font-medium text-gray-700">Campanha</label>
            <input
              id="busca"
              v-model="busca"
              type="text"
              name="busca"
              placeholder="Buscar campanha"
              class="h-11 border border-gray-200 shadow-sm rounded-xl px-4 outline-none focus:border-blue-100 focus:ring-2 focus:ring-blue-100"
            />
          </div>

          <div class="flex flex-col gap-1">
            <label for="data_de" class="block text-sm font-medium text-gray-700">Data início</label>
            <input id="data_de" v-model="de" type="date" name="data_de" class="h-11 border border-gray-200 shadow-sm rounded-xl px-4 outline-none focus:border-blue-100 focus:ring-2 focus:ring-blue-100" />
          </div>

          <div class="flex flex-col gap-1">
            <label for="data_ate" class="block text-sm font-medium text-gray-700">Data fim</label>
            <input id="data_ate" v-model="ate" type="date" name="data_ate" class="h-11 border border-gray-200 shadow-sm rounded-xl px-4 outline-none focus:border-blue-100 focus:ring-2 focus:ring-blue-100" />
          </div>

          <div class="flex flex-col gap-1" v-if="$store.state.user.admin">
            <label class="text-sm font-medium text-gray-500">Centro de custo</label>
            <div class="relative group">
              <select
                id="centrocusto"
                v-model="centrocusto"
                name="centrocusto"
                class="w-full h-11 px-4 pr-10 border border-gray-200 rounded-xl bg-white bg-none text-gray-700 shadow-sm appearance-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100 cursor-pointer"
              >
                <option :value="null">Todos</option>
                <option v-for="centro in centroscusto" :key="centro._id" :value="centro._id">{{ centro.nome }}</option>
              </select>
              <svg class="select-icon w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <div class="flex flex-col items-stretch gap-2 md:col-span-4 sm:flex-row lg:col-span-1">
            <button
              type="button"
              @click="list"
              class="h-11 w-full rounded-xl font-medium text-white transition"
              :style="{ backgroundColor: primaryColor }"
              @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
              @mouseleave="$event.target.style.backgroundColor = primaryColor"
            >
              <span>Buscar</span>
            </button>

            <button type="button" @click="clearFilters" class="h-11 rounded-xl border border-gray-200 bg-white px-4 font-medium text-gray-700 transition hover:bg-gray-50">
              <span>Limpar</span>
            </button>
          </div>
        </div>
      </div>

      <div class="w-full overflow-hidden sm:rounded-lg border border-gray-200 bg-white shadow-sm">
        <div class="overflow-x-auto">
          <table class="w-full min-w-max text-sm">
            <thead class="border-b bg-gray-100 tracking-wider text-gray-600">
              <tr>
                <th class="px-6 py-4 text-left font-medium">Campanha</th>
                <th v-if="$store.state.user.lzv2.centrocusto" class="px-6 py-4 text-left font-medium">Centro de Custo</th>
                <th class="px-6 py-4 text-left font-medium">Disparo</th>
                <th class="px-6 py-4 text-center font-medium">{{ $store.getters.rotuloEnvio('Enviados', 'Enviados para fila') }}</th>
                <th class="px-6 py-4 text-center font-medium">Falhas</th>
                <th class="px-6 py-4 text-center font-medium">Status</th>
                <th class="px-6 py-4 text-center font-medium">Ações</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="campaign in campaigns" :key="campaign._id" class="border-b border-gray-100 transition hover:bg-slate-50">
                <td class="px-6 py-5">
                  <div class="flex items-center gap-4">
                    <div class="flex h-11 w-11 items-center justify-center rounded-2xl bg-blue-50 text-sm font-semibold text-blue-700">
                      {{ getCampaignInitials(campaign.nome) }}
                    </div>
                    <div class="flex flex-col">
                      <span class="font-medium text-gray-800">{{ campaign.nome }}</span>
                      <span class="text-xs text-gray-400">{{ campaign.total }} contatos</span>
                    </div>
                  </div>
                </td>
                <td v-if="$store.state.user.lzv2.centrocusto" class="px-6 font-medium text-gray-700">
                  {{ campaign.centrocusto && campaign.centrocusto.nome ? campaign.centrocusto.nome : "" }}
                </td>
                <td class="px-6 text-gray-700">
                  {{
                    !campaign.chat && !campaign.api && campaign.dataHoraDisparo
                      ? `${moment(campaign.dataHoraDisparo).format("DD/MM/YYYY")} as
                  ${moment(campaign.dataHoraDisparo).format("HH:mm")}`
                      : campaign.possuiAgendamento
                      ? `${moment(campaign.dataHoraDisparo).format("DD/MM/YYYY")} as
                  ${moment(campaign.dataHoraDisparo).format("HH:mm")}`
                      : ""
                  }}
                </td>
                <td class="px-6 text-center font-medium text-gray-700">
                  {{ campaign.totalEnviado }}
                </td>
                <td class="px-6 text-center font-medium text-gray-700">
                  {{ campaign.totalFalhas }}
                </td>
                <td class="px-6 text-center">
                  <span :class="statusClass(returnStatus(campaign))" class="rounded-full px-3 py-1 text-xs font-medium">
                    {{ returnStatus(campaign) }}
                  </span>
                </td>
                <td class="px-6 py-5">
                  <div class="flex flex-wrap justify-center gap-2">
                    <button
                      type="button"
                      @click="$router.push({ path: `/${route}/detalhes/${campaign._id}`, query: { api: campaign.api } })"
                      class="rounded-lg px-4 py-2 text-sm font-medium text-white transition"
                      :style="{ backgroundColor: primaryColor }"
                      @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
                      @mouseleave="$event.target.style.backgroundColor = primaryColor"
                    >
                      Detalhes
                    </button>

                    <a
                      v-if="campaign.arquivoUpload && campaign.arquivoUpload.filename"
                      :href="`${apilz}/v1/campanhasLZV2/download/arquivo/${campaign._id}?token=${$store.state.token}`"
                      target="_blank"
                      rel="noopener noreferrer"
                      role="button"
                      class="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                      :title="campaign.arquivoUpload.originalname || 'Baixar arquivo original'"
                    >
                      Arquivo original
                    </a>

                    <button v-if="campaign.total > 40000" type="button" class="rounded-lg px-4 py-2 text-sm font-medium text-white transition" :style="{ backgroundColor: mixPrimaryColor('#ffffff', 0.35) }" @mouseenter="$event.target.style.backgroundColor = primaryColor" @mouseleave="$event.target.style.backgroundColor = mixPrimaryColor('#ffffff', 0.35)" @click="downloadResultado(campaign._id)">
                      Analítico
                    </button>

                    <a
                      v-else
                      target="_blank"
                      role="button"
                      :href="`${apilz}/v1/campanhasLZV2/download-resultado-url/${campaign._id}?token=${$store.state.token}`"
                      class="rounded-lg px-4 py-2 text-sm font-medium text-white transition"
                      :style="{ backgroundColor: mixPrimaryColor('#ffffff', 0.35) }"
                      @mouseenter="$event.target.style.backgroundColor = primaryColor"
                      @mouseleave="$event.target.style.backgroundColor = mixPrimaryColor('#ffffff', 0.35)"
                      >Analítico</a
                    >

                    <button v-if="returnPermiteExcluir(campaign)" type="button" @click="remove(campaign._id)" class="rounded-lg bg-red-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-red-600">
                      Excluir
                    </button>
                  </div>
                </td>
              </tr>

              <tr v-if="!campaigns.length">
                <td :colspan="$store.state.user.lzv2.centrocusto ? 7 : 6" class="px-6 py-12 text-center text-sm text-gray-400">Nenhuma campanha encontrada com os filtros atuais.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <AppPagination :current-page="page" :total-pages="totalPaginas" :total-items="total" :page-size="perPage" item-label="campanhas" @paginate="setPage" />

      <div v-if="isLoading" class="fixed left-0 top-80 z-50 w-full text-center text-lg font-bold text-zinc-600">
        <p class="text-zinc-200">Aguarde o fim do download!</p>
        <span class="text-green-600">{{ progresso }}</span> / 100%
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import AppPagination from "@/components/AppPagination.vue";
import { colorMixin } from "@/utils/colorMixin";

export default {
  mixins: [colorMixin],
  components: {
    AppPagination
  },
  computed: {
    cards() {
      const totalMensagens = this.relatorio.total;
      const enviados = this.relatorio.totalEnviados;
      const falhas = this.relatorio.totalNaoEnviados;

      return [
        {
          title: "Total de Campanhas",
          value: this.total,
          icon: "menu",
          iconClass: "text-blue-600"
        },
        {
          title: "Total de mensagens",
          value: totalMensagens,
          icon: "graph-bar",
          iconClass: "text-indigo-600"
        },
        {
          title: this.$store.getters.rotuloEnvio("Enviados", "Enviados para fila"),
          value: enviados,
          icon: "check-circle",
          iconClass: "text-green-600"
        },
        {
          title: "Falhas",
          value: falhas,
          icon: "x-circle",
          iconClass: "text-red-600"
        }
      ];
    },
    totalPaginas() {
      return Math.max(1, Math.ceil(this.total / this.perPage));
    }
  },
  data() {
    return {
      route: "campanhas",
      moment: moment,
      campaigns: [],
      centroscusto: [],
      centrocusto: null,
      busca: "",
      de: moment().startOf("M").format("YYYY-MM-DD"),
      ate: moment().endOf("M").format("YYYY-MM-DD"),
      role: "user",
      total: 0,
      relatorio: {
        total: 0,
        totalEnviados: 0,
        totalNaoEnviados: 0
      },
      page: 1,
      perPage: 20,
      apilz: window.location.hostname === "uno.trestto.com.br" ? "https://uno.trestto.com.br/api" : process.env.VUE_APP_API,
      isLoading: false,
      progresso: 0
    };
  },
  methods: {
    clearFilters() {
      this.busca = "";
      this.de = moment().startOf("M").format("YYYY-MM-DD");
      this.ate = moment().endOf("M").format("YYYY-MM-DD");
      this.centrocusto = this.$store.state.user.centrocusto || null;
      this.list();
    },

    getCampaignInitials(nome) {
      if (!nome) return "--";
      return nome.slice(0, 2).toUpperCase();
    },

    statusClass(status) {
      if (status === "Concluida") return "bg-green-100 text-green-700";
      if (status === "Em processamento") return "bg-yellow-100 text-yellow-700";
      if (status === "Agendada") return "bg-blue-100 text-blue-700";
      return "bg-gray-100 text-gray-700";
    },

    async list() {
      const campaignsReq = await this.$http.post("/v1/campanhasLZV2/list", {
        limit: this.perPage,
        centrocusto: this.centrocusto,
        busca: this.busca,
        de: this.de,
        ate: this.ate
      });
      this.campaigns = campaignsReq.data.data;
      this.total = campaignsReq.data.total;
      this.relatorio = campaignsReq.data.relatorio || this.relatorio;
      this.page = 1;
    },

    async downloadResultado(campaign) {
      const response = await this.$http.get(`${this.apilz}/v1/campanhasLZV2/download-resultado/${campaign}`);

      if (response.data.success) {
        const loader = await this.$loading.show({
          container: null,
          canCancel: true
        });

        await this.sockets.subscribe(response.data.socket_campanha, (data) => {
          this.isLoading = true;
          this.progresso = data.progress;
        });

        await this.sockets.subscribe(response.data.socket_campanha_ready, (data) => {
          this.isLoading = false;
          loader.hide();
          window.location.href = `${this.apilz}/upload?mimetype=text/csv&filename=${data.file}&folder=relatorios&original=${data.file}`;
        });

        console.log(response.data);
      }
    },

    async remove(id) {
      if (confirm("Tem certeza que deseja excluir essa campanha ?")) {
        const resp = await this.$http.delete(`/v1/campanhasLZV2/delete/${id}`);
        if (!resp.data.success) {
          this.$vToastify.error(resp.data.err);
          return;
        }
        this.$vToastify.success("Campanha removida");
        this.list();
      }
    },

    async setPage(page) {
      this.page = page;

      const skip = (page - 1) * this.perPage;
      const limit = this.perPage;

      const campaignsReq = await this.$http.post(`/v1/campanhasLZV2/list`, {
        limit,
        skip,
        centrocusto: this.centrocusto,
        busca: this.busca,
        de: this.de,
        ate: this.ate
      });

      this.campaigns = campaignsReq.data.data;
      this.total = campaignsReq.data.total;
      this.relatorio = campaignsReq.data.relatorio || this.relatorio;
    },

    returnPermiteExcluir(data) {
      const today = moment();
      if (moment(data.dataHoraDisparo) > today) return true;
      else return false;
    },
    returnStatus(data) {
      const today = moment();

      if (data.totalEnviado === 0 && today < moment(data.dataHoraDisparo)) {
        return "Agendada";
      } else if (data.ready && (data.totalEnviado === 0 || data.totalEnviado > 0) && !data.finalizado && today > moment(data.dataHoraDisparo)) {
        if (data.emProcessamento || data.totalEnviado > 0) return "Em processamento";
        else return "Agendada";
      } else if ((data.totalEnviado > 0 || data.totalFalhas > 0) && data.finalizado && today > moment(data.dataHoraDisparo)) {
        return "Concluida";
      } else if (data.totalEnviado > 0 || data.totalFalhas > 0) {
        return "Em andamento";
      }
    }
  },
  async beforeMount() {
    const centroscustoReq = await this.$http.post(`/v1/centrocusto/list`, { all: true });
    this.centroscusto = centroscustoReq.data.data;

    if (this.$store.state.user.centrocusto) {
      this.centrocusto = this.$store.state.user.centrocusto;
    }

    this.list();
  },
  mounted() {
    const user = this.$store.state.user || {};
    const usuarioId = user.usuario || user._id;
    if (!usuarioId) return;
    this.sockets.subscribe(`campanhaV2-update-${usuarioId}`, (update) => {
      const index = this.campaigns.findIndex((campaign) => String(campaign._id) === String(update._id));
      // A tela é paginada e filtrada. Eventos de campanhas fora da página — em especial
      // OFICIAL_FILHA, que a API nunca retorna — não devem recarregar a listagem inteira.
      if (index === -1) return;
      this.$set(this.campaigns, index, { ...this.campaigns[index], ...update });
    });
  },
  beforeDestroy() {
    const user = this.$store.state.user || {};
    const usuarioId = user.usuario || user._id;
    if (usuarioId) this.sockets.unsubscribe(`campanhaV2-update-${usuarioId}`);
  }
};
</script>

<style scoped>
#centrocusto {
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: none !important;
}

#centrocusto::-ms-expand {
  display: none;
}

.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  pointer-events: none;
  color: #9ca3af;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

.group:focus-within .select-icon {
  color: #6366f1;
  transform: translateY(-50%) rotate(180deg);
}
</style>
